import type { TcValidation, Trade, TradeCapture, TradeCaptureValidationObject } from '@/store/types.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';
import type { BidAsk, CurrencyChoice, PredealCheck, PriceType } from '@/types/common.ts';
import { ALL_CALENDAR_TENORS, type ISODate, type Tenor } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import type { Timestamp } from '@/utils/date.ts';

export type OptionState = OptionById;

export type OptionId = string;

export type OptionById = Record<OptionId, FxOption>;

export type OptionProductStrategy = 'Straddle' | 'Strangle' | 'RiskReversal';
export type OptionProductName = 'Vanilla' | 'Trigger' | 'Binary' | OptionProductStrategy;

export type OptionHedgeErrors = TradeCaptureValidationObject<Omit<OptionHedge, 'legId'>> & { legId: string };
export type OptionBarrierErrors = Record<string, TcValidation[]>;
export type OptionLegError = Omit<
  TradeCaptureValidationObject<Omit<OptionLegProperties, 'legId'>>,
  'barrier1' | 'barrier2'
> & {
  legId: LegId;
  barrier1?: OptionBarrierErrors;
  barrier2?: OptionBarrierErrors;
};

export type OptionErrors = {
  hedges: OptionHedgeErrors[];
  legs: OptionLegError[];
};
export type OptionTradeCapture = TradeCapture<OptionProperties, OptionErrors>;

export type FxOption = Trade<OptionProductName, OptionProperties, OptionErrors>;

export type FxOptionProductName = FxOption['type'];

export type OptionSide = 'Buy' | 'Sell';
export type OptionWay = 'Bid' | 'Ask';
const optionTypes = ['Call', 'Put'] as const;
export type OptionType = (typeof optionTypes)[number];
export type SettlementType = 'Cash' | 'Physical' | 'Forward' | 'NDF';

export type PossibleProperty<T = string> = {
  default: T;
  possible: T[];
};

export type HedgeType = 'Forward' | 'Spot' | 'Live' | 'Ndf';

// `${tileId}/${legId}`
export type LegId = `${string}/${number}`;

export type LegPropertiesSliceWithId = Partial<OptionLegProperties> & { legId: LegId };

export const strikeShortcuts = ['ATM', 'ATMF', 'ATMS'] as const;
export type StrikeShortcut = (typeof strikeShortcuts)[number];

export const premiumTenors: string[] = ['FwdPrem', ...ALL_CALENDAR_TENORS];

export type StrikeSolveState = 'notSolved' | 'solving';

export type OptionStrike =
  | { type: 'number'; value: number }
  | { type: 'delta'; value: number }
  | { type: 'shortcut'; value: StrikeShortcut };

export type SolvingSide =
  | { type: 'strategy' }
  | { type: 'compactView'; overrideSolveSide: OptionSide }
  | { type: 'riskReversal'; solveSide: 'BuyCall' | 'SellCall' };

export type SolvingStrike = {
  type: 'solving';
  way: OptionWay;
  solvingSide: SolvingSide;
  solveValue: number;
};

export type OptionLegProperties = {
  legId: LegId;
  legProductName: 'Vanilla' | 'Trigger' | 'Binary';
  type?: OptionType;
  side?: OptionSide;

  expiryDate?: ISODate;
  expiryDateTenor?: Tenor;

  deliveryDate?: ISODate;
  deliveryDateTenor?: Tenor;

  premiumPaymentDate?: ISODate;
  premiumPaymentDateTenor?: Tenor;

  strike?: OptionStrike | SolvingStrike;
  // TODO: remove
  strikeSolveState?: StrikeSolveState;

  notional?: number;
  notionalCurrencyIndex?: CurrencyIndex;

  premiumBid?: number;
  premiumAsk?: number;

  markup?: number;

  volatilityBid?: number;
  volatilityAsk?: number;

  barrier1?: OptionBarrier;
  barrier2?: OptionBarrier;
};

interface StreamingError {
  multipassErrorCode: string;
  errorCode: string;
  errorMessage: string;
  isSolving?: boolean;
}

export type OptionRfs =
  | { status: 'AWAITING'; rfsId: string; quoteType: 'Indicative' | 'Tradeable'; traderId?: string }
  | {
      status: 'PRICING';
      rfsId: string;
      quote: OptionQuote;
      quoteType: 'Indicative' | 'Tradeable';
      traderId?: string;
      initialRfsWindow: number;
    }
  | { status: 'EXPIRED'; quote: OptionQuote }
  | { status: 'CANCELLED' }
  | { status: 'ERROR'; rfsId?: string; error: StreamingError };

export type PremiumCategory = PriceType;

export interface PremiumType {
  premiumCategory: PremiumCategory;
  premiumCurrency: CurrencyIndex;
}

interface MifidRecord {
  date: string;
  isError?: boolean;
}

interface OptionMifid {
  rfsDate?: string;
  records: MifidRecord[];
}

export type OptionBarrier = {
  level?: number;
  type?: 'UpOut' | 'DownOut' | 'UpIn' | 'DownIn';
  nature?: 'AMER' | 'EURO' | 'WINDOW';
  possibleTypes?: PossibleProperty<OptionBarrier['type']>;
  possibleNatures?: PossibleProperty<OptionBarrier['nature']>;
  endDate?: ISODate;
  startDate?: ISODate;
};

export interface OptionProperties {
  tileId: string;
  currencyPair: string;

  legs: OptionLegProperties[];

  tiering?: string;
  isEspDefaultTiering?: boolean;

  rfs: OptionRfs;
  rfsVersion: number;
  mifid: OptionMifid;

  negotiatedCurrency?: CurrencyIndex;
  /** Requested premium unit: used by the price-type control and TC/RFS requests. */
  premiumType: PremiumType;
  /** TC leaf unit for group totals only (collapsed vertical or open/closed horizontal), not individual legs. */
  groupedPremiumType?: PremiumType;

  amountReference?: number;
  isAmountReferenceOverridden?: boolean;
  amountReferenceCurrency?: string;

  settlementType?: SettlementType;
  possibleSettlementTypes?: PossibleProperty<SettlementType>;

  isCrossed?: boolean;
  crossCurrency?: string;
  crossCurrenciesList?: string[];

  fixingReference1?: string;
  fixingReference1Default?: string;
  fixingReference1List?: string[];
  fixingReference2?: string;
  fixingReference2Default?: string;
  fixingReference2List?: string[];

  cashSettlementCurrency?: string;
  possibleCashSettlementCurrencies?: PossibleProperty;

  cutOffMarketPlace?: string;
  possibleCutOffMarketPlaces?: PossibleProperty;

  hedges: OptionHedge[];
  hedgeType: HedgeType;
  markupCurrency?: string;

  maxDate?: string;

  /* Duplicated from legs to allow modification at global level */
  /* BUG? iso FX. We only save the date at the global level not at leg level */
  premiumPaymentDate?: ISODate;
  premiumPaymentDateTenor?: Tenor;

  uiState: OptionUiState;
}

export interface OptionHedge {
  legId: string;
  rate?: number;
  maturityDate?: ISODate;
  notionalAmount?: number;
  notionalCurrency?: CurrencyChoice;
}

export type RfsStatus =
  | 'NOT_PRICEABLE'
  | 'PRICEABLE'
  | 'AWAITING_INDICATIVE'
  | 'AWAITING_EXECUTABLE'
  | 'STREAMING_INDICATIVE'
  | 'STREAMING_EXECUTABLE'
  | 'ERROR'
  | 'BYPASSABLE_ERROR';

export type LegsData = {
  greeks: Greeks;
  marketData: MarketData;
  premiumData: PremiumData;
  marginData?: MarginData;
  strikeData: StrikeData;
  midVolatility?: MidVolatility;
  // solving: string | null;
};

export type QuoteCcy = {
  bidAmount: number;
  askAmount: number;
  bidPercent: number;
  askPercent: number;
  bidPps: number;
  askPps: number;
  deltaCcy: number;
  vegaCcy: number;
  gammaCcy: number;
  thetaCcy: number;
  bidMarkup: number;
  askMarkup: number;
};

export type QuoteType = 'Tradeable' | 'Indicative';
export type MultipassLegId = `${string}/0/${number}` | `${string}/0/call` | `${string}/0/put`;

export type OptionQuote = {
  requestId: string;
  // traderId: string | null;
  quoteId: string;
  quoteType: QuoteType;
  rfsWindow: number;
  predealChecks: PredealCheck[];
  frontTimestamp: Timestamp;
  backTimestamp: number;
  contribution: 'Automatic' | 'Manual';
  tiering: 'STREAMDEFAULT' | string;

  ccy1: QuoteCcy;
  ccy2: QuoteCcy;

  spot: BidAsk<number>;
  hedgesData: HedgeData[];
  legsData: Record<MultipassLegId, LegsData>;
};

export type HedgeData = {
  rateMid?: number;
  notionalCurrency?: string;
  notionalAmount?: number;
  maturityDate?: string;
};

export interface PremiumData {
  bidAmountCcy1: number;
  askAmountCcy1: number;
  bidAmountCcy2: number;
  askAmountCcy2: number;

  bidPercentCcy1: number;
  askPercentCcy1: number;
  bidPercentCcy2: number;
  askPercentCcy2: number;

  bidPpsCcy1: number;
  askPpsCcy1: number;
  bidPpsCcy2: number;
  askPpsCcy2: number;
}

interface Greeks {
  vegaCcy1: number;
  vegaCcy2: number;
  deltaCcy1: number;
  deltaCcy2: number;
  gammaCcy1: number;
  gammaCcy2: number;
  thetaCcy1: number;
  thetaCcy2: number;
}

interface MarketData {
  volatility: BidAsk<number>;
  forward: BidAsk<number>;
  swapPoint: BidAsk<number>;
  zeroCouponCcy1: number;
  zeroCouponCcy2: number;
}

interface MarginData {
  bidMarkupCcy1: number | null;
  askMarkupCcy1: number | null;
  bidMarkupCcy2: number | null;
  askMarkupCcy2: number | null;
  bidMarkupInBp: number | null;
  askMarkupInBp: number | null;
}

interface StrikeData {
  absoluteStrikePrice: number;
  deltaStrikePrice: string;
  solvedStrike: number | null;
}

interface MidVolatility {
  atmVolatilityMid: number | null;
  smiledVolatilityMid: number | null;
}

export type StrategyPanelMode = 'hidden' | 'vertical' | 'horizontal';

export type OptionUiState = {
  strategyPanelMode: StrategyPanelMode;
  legsNotionalCurrenciesLinkState: 'linked' | 'unlinked';
  groupLegsByExpiry: boolean;
  expandGreekAndMarketPanels: boolean;
};

export type OptionUserPreference = Pick<
  UserPreferencesState,
  // Expand Greeks and market data by default
  | 'optionExpandGreekAndMkt'
  // Default price type
  | 'defaultPriceType'
  // Default Hedge type
  | 'defaultHedgeType'
  // Group legs by expiry date by default
  | 'optionStrategyGroupLegs'
  // TODO: ?
  // | 'optionOneWayQuotation'
  // | 'optionAlignCurrencies'
  // | 'optionIsPremiumNetted'
  // | 'optionDisplayMidVolatility'
>;
